import * as React from 'react';

import { translate } from '~l10n/client';

import { PaywallScreens } from '~analytics/types';
import { UIButton } from '~shared/components/buttons';
import { DialogProps } from '~shared/components/Dialog';
import {
  Feature,
  PaywallDialog,
  PaywallFeatureList,
  PaywallTouch,
  PaywallUpgradeButton,
} from '~teacher/components/PaywallDialog';

type DialogContent = [string, string, Array<Feature>, PaywallTouch];

export type Props = Pick<DialogProps, 'open' | 'onClose'> & {
  paywallTouch: PaywallTouch;
};

function getDialogContent(paywall: PaywallTouch): DialogContent {
  const [primaryPaywall] = paywall;
  switch (primaryPaywall) {
    case PaywallScreens.RoomsAddRoom:
      return [
        translate('Upgrade to Add More Rooms'),
        translate(
          'Rooms allow you to launch multiple activities at the same time for all your classes. Upgrade to Socrative Pro and get more features:'
        ),
        [
          { text: translate('Email individual reports') },
          { text: translate('Manage student access to rooms') },
          { text: translate('One attempt on quizzes') },
        ],
        paywall,
      ];

    case PaywallScreens.RoomsShareRoom:
      return [
        translate('Upgrade to Share Room URL'),
        translate(
          'Generate a link to give your students direct access to this room. Upgrade to Socrative Pro and get more features:'
        ),
        [
          { text: translate('Run multiple quizzes at once') },
          { text: translate('Give partial marks') },
          { text: translate('Customize reporting') },
        ],
        paywall,
      ];

    case PaywallScreens.RoomsAddRoster:
    case PaywallScreens.AppHeaderAddRoster:
      return [
        translate('Upgrade to Create a Roster'),
        translate(
          'A Roster lets you choose which students have access to a room’s activities. Upgrade to Socrative Pro and get more features:'
        ),
        [
          { text: translate('Run multiple quizzes at once') },
          { text: translate('Give partial marks') },
          { text: translate('Customize reporting') },
        ],
        paywall,
      ];

    default:
      return ['', '', [], [null]];
  }
}

export function LongRoomsPaywall({ paywallTouch, ...props }: Props) {
  const [title, message, features, touch] = getDialogContent(paywallTouch);

  if (!paywallTouch) return null;

  return (
    <PaywallDialog.Long
      id="rooms-paywall"
      title={title}
      actions={[
        <UIButton key="cancel" onClick={props.onClose}>
          {translate('Cancel')}
        </UIButton>,
        <PaywallUpgradeButton
          key="upgrade"
          appearance="primary"
          paywallScreen={touch[0]}
        />,
      ]}
      closeLabel={translate('Close')}
      {...props}
      paywallTouch={touch}
    >
      <p className="m-0 pb-4 text-blue-900">{message}</p>
      <PaywallFeatureList features={features} />
    </PaywallDialog.Long>
  );
}
